import React, {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useState,

} from 'react';

import { toast } from 'sonner';

import { AuthContext } from '../../context/AuthContext';

import { useFeatureFlags } from '../../context/FeatureFlagsContext';

import { buildNavContext } from '../../navigation/navConfig';

import { ALL_WALKTHROUGH_TOURS } from './tours/tourRegistry';

import { filterStepsForUser } from './walkthroughPolicy';

import {

  loadWalkthroughState,

  markTourCompleted,

  markWalkthroughDismissed,

  resolveWalkthroughStorageKey,

  saveWalkthroughState,

} from './walkthroughStorage';

import {

  buildWalkthroughSequence,

  clearWalkthroughSession,

  findIndexAfterChapterTour,

  hasCompletedFirstRunWalkthrough,

  isChapterBreakStep,

  resolveStepRoute,

  setWalkthroughSession,

} from './walkthroughOrchestrator';

import {

  pullAndMergeWalkthroughPreferences,

  pushWalkthroughCompletionIfEligible,

} from './walkthroughSync';

import { t } from './i18n/walkthroughI18n';



const WalkthroughContext = createContext(null);



function notifyTourEnded() {
  toast.success(t('walkthrough.ui.toastComplete'));
}



/**

 * @param {import('./walkthroughTypes').ResolvedWalkthroughStep[]} steps

 * @param {number} currentIndex

 * @returns {string | null}

 */

function resolveCompletedTourId(steps, currentIndex) {

  const step = steps[currentIndex];

  if (!step?.tourId || isChapterBreakStep(step)) {

    return null;

  }



  const nextStep = steps[currentIndex + 1];

  if (!nextStep || nextStep.tourId !== step.tourId || isChapterBreakStep(nextStep)) {

    return step.tourId;

  }



  return null;

}



/**

 * @param {Object} props

 * @param {React.ReactNode} props.children

 * @param {import('./walkthroughTypes').WalkthroughTourDef[]} [props.tours]

 */

export function WalkthroughProvider({ children, tours = ALL_WALKTHROUGH_TOURS }) {

  const { user } = useContext(AuthContext);

  const { isWalkthroughSyncEnabled } = useFeatureFlags();

  const storageKey = resolveWalkthroughStorageKey(user);



  const [persistedState, setPersistedState] = useState(() =>

    loadWalkthroughState(storageKey)

  );

  const [activeTour, setActiveTour] = useState(null);



  useEffect(() => {

    let cancelled = false;

    const local = loadWalkthroughState(storageKey);



    async function loadAndMerge() {

      if (!storageKey) {

        if (!cancelled) {

          setPersistedState(local);

        }

        return;

      }



      const merged = await pullAndMergeWalkthroughPreferences(local, user);

      if (cancelled) return;



      saveWalkthroughState(storageKey, merged);

      setPersistedState(merged);

      setActiveTour(null);

      clearWalkthroughSession();

    }



    loadAndMerge();



    return () => {

      cancelled = true;

    };

  }, [isWalkthroughSyncEnabled, storageKey, user]);



  useEffect(() => {

    if (!activeTour) {

      clearWalkthroughSession();

      return;

    }



    setWalkthroughSession({

      active: true,

      allowedRoutes: activeTour.allowedRoutes ?? [],

    });

  }, [activeTour]);



  const markTourComplete = useCallback(

    (tourId, tourVersion = 1) => {

      if (!tourId) return;



      setPersistedState((prev) => {

        const next = markTourCompleted(prev, tourId, tourVersion);

        if (storageKey) {

          saveWalkthroughState(storageKey, next);

          if (hasCompletedFirstRunWalkthrough(user, next)) {

            pushWalkthroughCompletionIfEligible(next, user, isWalkthroughSyncEnabled).catch(() => {});

          }

        }

        return next;

      });

    },

    [isWalkthroughSyncEnabled, storageKey, user]

  );



  const startTour = useCallback(

    (tourId) => {

      if (!user) return;



      const tourDef = tours.find((tour) => tour.id === tourId);

      if (!tourDef) return;



      const ctx = buildNavContext(user);

      if (tourDef.visible && !tourDef.visible(user, ctx)) {

        return;

      }



      const steps = filterStepsForUser(tourDef.steps, user, ctx);

      if (steps.length === 0) return;



      const allowedRoutes = new Set();

      if (tourDef.prerequisiteRoute) {

        allowedRoutes.add(tourDef.prerequisiteRoute);

      }

      for (const step of steps) {

        if (step.route) {

          allowedRoutes.add(step.route);

        }

      }



      setActiveTour({

        tourId,

        tourIds: [tourId],

        tourVersion: tourDef.version ?? 1,

        steps,

        currentIndex: 0,

        paused: false,

        pauseReason: null,

        expectedRoute: null,

        isNavigating: false,

        allowedRoutes: [...allowedRoutes],

      });

    },

    [tours, user]

  );



  const startSequence = useCallback(

    (resolvedTours) => {

      if (!user || !Array.isArray(resolvedTours) || resolvedTours.length === 0) {

        return;

      }



      const { steps, allowedRoutes } = buildWalkthroughSequence(resolvedTours, user);

      if (steps.length === 0) return;



      setActiveTour({

        tourIds: resolvedTours.map((tour) => tour.id),

        steps,

        currentIndex: 0,

        paused: false,

        pauseReason: null,

        expectedRoute: null,

        isNavigating: false,

        allowedRoutes,

      });

    },

    [user]

  );



  const setNavigating = useCallback((isNavigating) => {

    setActiveTour((current) => {

      if (!current) return current;

      return { ...current, isNavigating };

    });

  }, []);



  const setExpectedRoute = useCallback((expectedRoute) => {

    setActiveTour((current) => {

      if (!current) return current;

      return { ...current, expectedRoute };

    });

  }, []);



  const pauseTour = useCallback((reason = 'manual-navigation') => {

    setActiveTour((current) => {

      if (!current) return current;

      return { ...current, paused: true, pauseReason: reason };

    });

  }, []);



  const resumeTour = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return current;

      const step = current.steps[current.currentIndex];

      const stepRoute = resolveStepRoute(step);

      return {
        ...current,
        paused: false,
        pauseReason: null,
        expectedRoute: stepRoute ?? current.expectedRoute,
      };

    });

  }, []);



  const advanceToIndex = useCallback(

    (nextIndex) => {

      setActiveTour((current) => {

        if (!current) return current;

        if (nextIndex >= current.steps.length) {

          notifyTourEnded();

          return null;

        }

        return {

          ...current,

          currentIndex: nextIndex,

          paused: false,

          pauseReason: null,

        };

      });

    },

    []

  );



  const next = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return current;

      if (current.currentIndex >= current.steps.length - 1) return current;



      const completedTourId = resolveCompletedTourId(current.steps, current.currentIndex);

      if (completedTourId) {

        const step = current.steps[current.currentIndex];

        markTourComplete(completedTourId, step.tourVersion ?? 1);

      }



      return {

        ...current,

        currentIndex: current.currentIndex + 1,

        paused: false,

        pauseReason: null,

      };

    });

  }, [markTourComplete]);



  const prev = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return current;

      if (current.currentIndex <= 0) return current;

      return { ...current, currentIndex: current.currentIndex - 1 };

    });

  }, []);



  const skipCurrentStep = useCallback(() => {

    next();

  }, [next]);



  const skipChapter = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return current;



      const step = current.steps[current.currentIndex];

      if (!isChapterBreakStep(step)) {

        return current;

      }



      if (step.nextTourId) {

        markTourComplete(step.nextTourId, 1);

      }



      const nextIndex = findIndexAfterChapterTour(current.steps, current.currentIndex);

      if (nextIndex >= current.steps.length) {

        notifyTourEnded();

        return null;

      }



      return {

        ...current,

        currentIndex: nextIndex,

        paused: false,

        pauseReason: null,

      };

    });

  }, [markTourComplete]);



  const complete = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return null;



      const completedTourId = resolveCompletedTourId(current.steps, current.currentIndex);

      if (completedTourId) {

        const step = current.steps[current.currentIndex];

        markTourComplete(completedTourId, step.tourVersion ?? 1);

      } else if (current.tourId) {

        markTourComplete(current.tourId, current.tourVersion ?? 1);

      }



      notifyTourEnded();

      return null;

    });

  }, [markTourComplete]);



  const endActiveTour = useCallback(() => {

    setActiveTour(null);

  }, []);



  const skip = useCallback(() => {

    setActiveTour((current) => {

      if (!current) return null;



      setPersistedState((prev) => {

        const next = markWalkthroughDismissed(prev);

        if (storageKey) {

          saveWalkthroughState(storageKey, next);

          pushWalkthroughCompletionIfEligible(next, user, isWalkthroughSyncEnabled).catch(() => {});

        }

        return next;

      });

      notifyTourEnded();

      return null;

    });

  }, [isWalkthroughSyncEnabled, storageKey, user]);



  const currentStep = activeTour?.steps[activeTour.currentIndex] ?? null;



  const value = useMemo(

    () => ({

      persistedState,

      startTour,

      startSequence,

      next,

      prev,

      skip,

      complete,

      endActiveTour,

      skipCurrentStep,

      skipChapter,

      resumeTour,

      pauseTour,

      setNavigating,

      setExpectedRoute,

      advanceToIndex,

      isActive: activeTour !== null,

      isPaused: activeTour?.paused ?? false,

      pauseReason: activeTour?.pauseReason ?? null,

      isNavigating: activeTour?.isNavigating ?? false,

      expectedRoute: activeTour?.expectedRoute ?? null,

      currentStep,

      activeTourId: activeTour?.tourId ?? currentStep?.tourId ?? null,

      stepIndex: activeTour?.currentIndex ?? -1,

      stepCount: activeTour?.steps.length ?? 0,

    }),

    [

      activeTour,

      advanceToIndex,

      complete,

      currentStep,

      endActiveTour,

      next,

      pauseTour,

      persistedState,

      prev,

      resumeTour,

      setExpectedRoute,

      setNavigating,

      skip,

      skipChapter,

      skipCurrentStep,

      startSequence,

      startTour,

    ]

  );



  return (

    <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>

  );

}



/**

 * @returns {{

 *   persistedState: import('./walkthroughTypes').WalkthroughPersistedState,

 *   startTour: (tourId: string) => void,

 *   startSequence: (tours: import('./walkthroughTypes').ResolvedWalkthroughTour[]) => void,

 *   next: () => void,

 *   prev: () => void,

 *   skip: () => void,

 *   complete: () => void,

 *   endActiveTour: () => void,

 *   skipCurrentStep: () => void,

 *   skipChapter: () => void,

 *   resumeTour: () => void,

 *   pauseTour: (reason?: string) => void,

 *   setNavigating: (isNavigating: boolean) => void,

 *   setExpectedRoute: (route: string | null) => void,

 *   advanceToIndex: (index: number) => void,

 *   isActive: boolean,

 *   isPaused: boolean,

 *   pauseReason: string | null,

 *   isNavigating: boolean,

 *   expectedRoute: string | null,

 *   currentStep: import('./walkthroughTypes').ResolvedWalkthroughStep | null,

 *   activeTourId: string | null,

 *   stepIndex: number,

 *   stepCount: number,

 * }}

 */

export function useWalkthrough() {

  const context = useContext(WalkthroughContext);



  if (context === null) {

    throw new Error('useWalkthrough must be used within a WalkthroughProvider');

  }



  return context;

}



export default WalkthroughContext;


